import { styled } from '@mui/material'

/**
 * SC-10890: lista de notificações e aberturas da cobrança.
 *
 * Uma grade só (`GRID`) descreve as colunas, e tanto a linha quanto as faixas a
 * usam — foi o que resolveu o desalinhamento entre cabeçalho e valores, que
 * aparecia quando cada parte tinha a sua própria grade.
 */

/**
 * Data · ícone · evento · ação.
 *
 * A data vem PRIMEIRO porque numa cronologia o "quando" é a chave de leitura —
 * à direita ela virava um dado acessório, e o olho tinha que atravessar a linha
 * para saber a ordem dos fatos.
 *
 * O status saiu da grade: virou texto na segunda linha do evento (padrão do
 * detalhe da cobrança). Quatro pílulas vermelhas em coluna dominavam a seção e
 * repetiam a mesma palavra quatro vezes.
 */
const GRID = '150px 22px minmax(0, 1fr) 34px'

export const List = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
}))

/**
 * Faixa que separa o antes e o depois do vencimento. Substitui as tabs
 * Pré/Pós-vencimento, que classificavam por string do nome da campanha
 * traduzido — qualquer campanha nova caía em "pós" sem aviso.
 */
export const Band = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 0 6px'
}))

interface ToneProps {
  tone?: 'overdue'
}

/**
 * Cor e caixa do rótulo da faixa. O TAMANHO vem do `Typography variant="xsBold"`
 * no componente — as variantes custom do tema não estão no tipo do MUI, e
 * escrever `fontSize` aqui é justamente o que tira o elemento da escala.
 */
export const BandLabel = styled('span')<ToneProps>(({ theme, tone }) => ({
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  // Vencido usa a família alert: é a informação que muda a conversa de cobrança.
  color: tone === 'overdue' ? '#B27C30' : theme.palette.neutral[300]
}))

export const BandLine = styled('div')<ToneProps>(({ tone }) => ({
  flexGrow: 1,
  borderTop: `1px solid ${tone === 'overdue' ? '#F0E2C8' : '#E3E6E3'}`
}))

/**
 * Linha do evento.
 *
 * Sem fundo por tipo: o realce colorido existia para dar corpo às linhas de
 * abertura e resposta, que ficavam com colunas vazias quando o status era uma
 * coluna própria. Com o status em texto, a linha se sustenta sozinha e a cor
 * virava ruído — o ícone já distingue o tipo do evento.
 */
export const Row = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: GRID,
  alignItems: 'center',
  columnGap: '10px',
  padding: '8px 12px',
  borderRadius: '5px',

  '&:hover': {
    background: theme.palette.neutral[800]
  },

  // Quatro colunas não cabem em 400px: no celular a data encabeça a linha e o
  // resto empilha embaixo.
  // Três colunas no celular: ícone, evento e AÇÃO. A ação precisa da coluna
  // própria à direita — empurrada para baixo do texto ela gastava uma terceira
  // linha por evento, e altura é o recurso escasso no telefone.
  [theme.breakpoints.down(620)]: {
    gridTemplateColumns: '22px minmax(0, 1fr) 34px',
    rowGap: '2px'
  }
}))

export const IconCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  // No celular o ícone do canal abre a linha, à ESQUERDA da data: é o tipo da
  // notificação (e-mail, WhatsApp, SMS) e é por ele que se varre a lista para
  // conferir por onde saiu. Na segunda linha, ao lado do texto, ele ficava no
  // meio do bloco e perdia essa leitura de coluna.
  [theme.breakpoints.down(620)]: {
    gridColumn: 1,
    gridRow: 1,
    alignItems: 'center'
  }
}))

export const EventCell = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  minWidth: 0,

  // Abaixo do par ícone+data, ocupando as duas primeiras colunas: a terceira
  // fica reservada para a ação.
  [theme.breakpoints.down(620)]: {
    gridColumn: '1 / 3',
    gridRow: 2
  }
}))

/** `tabular-nums` mantém os dígitos alinhados entre as linhas. */
export const DateCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down(620)]: {
    // Segunda coluna da primeira linha, ao lado do ícone do canal.
    gridColumn: 2,
    gridRow: 1
  }
}))

/**
 * Coluna reservada em todas as linhas para que os botões fiquem na mesma
 * vertical — antes a ação seguia o fim do texto e mudava de posição a cada
 * linha. A largura acomoda "Enviar agora", o rótulo mais largo.
 */
export const ActionCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  // À direita: a coluna é mais larga que o botão, e com flex-start ele ficava
  // encostado à esquerda dela, deixando um vão até a borda da seção.
  justifyContent: 'flex-end',
  [theme.breakpoints.down(620)]: {
    // Coluna própria à direita, alinhada com o topo do bloco de texto.
    gridColumn: 3,
    gridRow: 2,
    justifyContent: 'flex-end',
    alignItems: 'start'
  }
}))

/**
 * Bloco da resposta do cliente — a única "fala" da lista, então ganha fundo e
 * borda completa fina. Nunca borda-esquerda de 3px, que o DESIGN.md reserva ao
 * Accordion.
 */
export const ReplyBubble = styled('div')(({ theme }) => ({
  marginTop: '4px',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #D9E5F5',
  background: '#fff',
  color: theme.palette.neutral[500],
  whiteSpace: 'pre-line',
  wordBreak: 'break-word'
}))

/**
 * Ação de reenvio: só o ícone.
 *
 * Com rótulo visível, "Reenviar" e "Enviar agora" tinham larguras diferentes e
 * os botões saíam de tamanhos distintos entre as linhas. Quadrado de 26px,
 * mesma medida em todas — o texto vai no tooltip e no aria-label.
 */
export const ResendIconButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  padding: 0,
  border: `1px solid ${theme.palette.neutral[700]}`,
  borderRadius: '5px',
  background: '#fff',
  color: theme.palette.neutral[400],
  cursor: 'pointer',
  transition: 'border-color .15s, background .15s, color .15s',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    background: '#E6FDEA',
    color: '#1A803C'
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '1px'
  }
}))
